import React from 'react';
import { TreeView } from './';
import { flattenTree } from './utils';
import { styled, Theme, useStyletron } from '../../../Themes';
import { MIcon } from '../../../Components/Icon';
import { paddingSize } from '../../../Utilities/utils/Theming';
export { TreeView };
export { flattenTree };

export const TreeItem = styled<any, 'div', typeof Theme>('div', ({ ...props }: any) => {
	const isSelected =
		props['aria-selected'] || props['data-selected'] || props.className.indexOf('tree-node--selected') >= 0;
	const verticalPadding =
		props.size && props.size !== 'default'
			? `var(--epods--treeview-${props.size}-item-padding-v)`
			: 'var(--epods--treeview-item-padding-v)';
	const horizontalPadding =
		props.size && props.size !== 'default'
			? `var(--epods--treeview-${props.size}-item-padding-h)`
			: 'var(--epods--treeview-item-padding-h)';
	return {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		opacity: props.disabled ? 0.5 : 1,
		...paddingSize(verticalPadding, horizontalPadding, verticalPadding, horizontalPadding),
		fontFamily: 'var(--epods--font-base-semibold)',
		cursor: 'pointer',
		color: isSelected ? 'var(--epods--color-text-primary)' : 'var(--epods--color-text-primary)',
		backgroundColor: isSelected ? 'var(--epods--color-bg-active)' : 'var(--epods--color-bg-transparent)',
		borderRightStyle: 'var(--epods--treeview-border-style)',
		borderRightWidth: 'var(--epods--treeview-border-width)',
		borderRightColor: isSelected ? 'var(--epods--color-bg-primary)' : 'transparent', //|| props.className.indexOf('tree-node--last') >= 0
		':hover': {
			backgroundColor: 'var(--epods--color-bg-hover)',
		},
	} as any;
});

export const TreeItemElement = styled<any, 'div', typeof Theme>('div', ({ ...props }: any) => {
	return {
		marginRight: 'var(--epods--space-inset-s)',
	};
});

export const LabelIcon = ({ icon }: any) => {
	return <MIcon icon={icon} size="24px" color="var(--epods--color-text-ds-blue)" className="mr-s"></MIcon>;
};

export const LabelContainer = (props: any) => {
	const { children, ...rest } = props;
	return (
		<div
			{...rest}
			data-epo="treeview-label-container"
			part="treeview-label-container"
			style={{
				flexGrow: 1,
				display: 'flex',
				alignItems: 'center',
				overflow: 'hidden',
				padding: '2px',
				margin: '-2px',
			}}
		>
			{children}
		</div>
	);
};

export const LabelActions = (props: any) => {
	return (
		<div
			{...props}
			data-epo="treeview-label-actions"
			part="treeview-label-actions"
			style={{ marginLeft: 'var(--epods--space-inset-s)', display: 'flex' }}
			onKeyDown={(e: any) => {
				e.stopPropagation();
			}}
			onClick={(e: any) => {
				e.stopPropagation();
			}}
		></div>
	);
};

export const Label = ({ ...args }: any) => {
	const [css] = useStyletron();
	return (
		<span
			{...args}
			data-epo="treeview-label-label"
			part="treeview-label-label"
			className={
				css({
					flexGrow: 1,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					fontFamily: 'var(--epods--font-base-semibold)',
					fontSize: 'var(--epods--font-size-p)',
					lineHeight: 'var(--epods--line-heiht-p)',
				}) +
				' ' +
				args.className
			}
		></span>
	);
};

export const LinkLabel = ({ ...args }: any) => {
	const [css] = useStyletron();
	return (
		<a
			{...args}
			data-epo="treeview-label-label"
			part="treeview-label-label"
			className={
				css({
					flexGrow: 1,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					fontFamily: 'var(--epods--font-base-semibold)',
					fontSize: 'var(--epods--font-size-p)',
					lineHeight: 'var(--epods--line-heiht-p)',
				}) +
				' ' +
				args.className
			}
		></a>
	);
};

export const LabelArrow = ({ isOpen, isBranch, onClick }: any) => {
	const [css] = useStyletron();
	if (!isBranch) {
		return <div className="mr-s" style={{ width: '18px' }}></div>;
	}
	const clickProps = onClick
		? {
				onClick: (e: any) => {
					e.stopPropagation();
					onClick(e);
				},
				tabIndex: 0,
				role: 'button',
				onKeyDown: (e: any) => {
					e.stopPropagation();
					onClick(e);
				},
		  }
		: {};
	return isOpen ? (
		<MIcon
			icon="keyboard_arrow_down"
			size="18px"
			title="arrow_down"
			color="var(--epods--color-text-ds-blue)"
			className={
				'mr-s ' + onClick &&
				css({
					':hover': { backgroundColor: 'var(--epods--color-text-inverted)' },
					marginRight: 'var(--epods--space-inset-s)',
				})
			}
			{...clickProps}
		></MIcon>
	) : (
		<MIcon
			icon="keyboard_arrow_right"
			size="18px"
			title="arrow_right"
			color="var(--epods--color-text-ds-blue)"
			className={
				'mr-s ' + onClick &&
				css({
					':hover': { backgroundColor: 'var(--epods--color-text-inverted)' },
					marginRight: 'var(--epods--space-inset-s)',
				})
			}
			{...clickProps}
		></MIcon>
	);
};

export const LabelCheckBox = ({ variant, ...rest }: any) => {
	const [css] = useStyletron();
	switch (variant) {
		case 'all':
			return (
				<span
					data-epo="epo-checkbox"
					part="epo-checkbox"
					className={css({
						display: 'flex',
						height: '18px',
						width: '18px',
						borderRadius: 'var(--epods--border-radius)',
						backgroundColor: 'var(--epods--color-bg-primary)',
						marginRight: 'var(--epods--space-inset-s)',
						paddingLeft: '0px',
						cursor: 'pointer',
					})}
					{...rest}
				>
					<MIcon icon="check" color="var(--epods--color-text-inverted)" size="18px" />
				</span>
			);
		case 'none':
			return (
				<span
					data-epo="epo-checkbox"
					part="epo-checkbox"
					className={css({
						display: 'flex',
						height: '18px',
						width: '18px',
						borderRadius: 'var(--epods--border-radius)',
						backgroundColor: 'var(--epods--color-bluegrey-200)',
						marginRight: 'var(--epods--space-inset-s)',
						paddingLeft: '0px',
						cursor: 'pointer',
					})}
					{...rest}
				>
					<MIcon icon="check" color="transparent" size="18px" />
				</span>
			);
		case 'some':
			return (
				<span
					data-epo="epo-checkbox"
					part="epo-checkbox"
					className={css({
						display: 'flex',
						height: '18px',
						width: '18px',
						borderRadius: 'var(--epods--border-radius)',
						backgroundColor: 'var(--epods--color-bg-primary)',
						marginRight: 'var(--epods--space-inset-s)',
						paddingLeft: '0px',
						cursor: 'pointer',
					})}
					{...rest}
				>
					<MIcon icon="remove" color="var(--epods--color-text-inverted)" size="18px" />
				</span>
			);
		default:
			return null;
	}
};
